import {
  TransformationTemplate,
  AssetType,
  CTA,
  ProviderLogo,
} from '@amzn/fire-tv-campaign-automation-service';
import { KatDropdown, KatFileUpload } from '@amzn/katal-react';

import { PreviewConstants } from './constants';

enum InputType {
  UPLOAD = 'upload',
  URL = 'url',
  SEARCH = 'search',
  LOGO_TEXT = 'logo-text',
  TEXT = 'text',
  DROP_DOWN = 'drop-down',
  DATE_TIME = 'date-time',
}

/**
 * Enumeration of possible asset processing states in the asset input workflow.
 *
 * @enum {string}
 */
enum AssetStatus {
  COMPLETED = 'COMPLETED',

  FAILED = 'FAILED',

  PENDING_INPUT = 'PENDING_INPUT',

  PENDING_UPLOAD = 'PENDING_UPLOAD',

  PENDING_VALIDATION = 'PENDING_VALIDATION',

  BASIC_VALIDATION_FAILED = 'BASIC_VALIDATION_FAILED',

  PENDING_TRANSFORMATION = 'PENDING_TRANSFORMATION',

  TRANSFORMATION_IN_PROGRESS = 'TRANSFORMATION_IN_PROGRESS',
}

interface IAssetInputHandler {
  onTransformationTemplateChange(
    transformationTemplateChangeEvent: KatDropdown.ChangeEvent,
    assetDetails: AssetDetails,
    status: AssetStatus,
  ): void;
  clearError(): void;
  handleFileAttached(fileAttachedEvent: KatFileUpload.FilesAttachedEvent): void;
  handleFileAttachedError(fileAttachedErrorEvent: KatFileUpload.FilesAttachedErrorEvent): void;
  resetAssetState(): void;
  handleFileReplaced(fileReplacedEvent: KatFileUpload.FilesReplacedEvent): void;
  handleUrl(urlInput: string): void;
}

interface LogoTextState {
  logoTitle?: string;
  logoDescription?: string;
  cta?: CTA;
  providerLogo?: ProviderLogo;
}

interface AssetState extends LogoTextState {
  key?: string;
  error?: string;
  status: AssetStatus;
  transformationTemplate: TransformationTemplate;
  startTime?: string;
  endTime?: string;
  videoDuration?: number;
}

interface Coordinates {
  x: number;
  y: number;
}

interface StateConfig {
  containerOffset: number;
  logo: { x: number; y: number };
  background: { x: number; y: number };
  overlay: { x: number; y: number };
}

/**
 * Interface representing the preview assets for a campaign
 * @interface PreviewAssets
 */
interface PreviewAssets {
  /** Optional URL for the background image asset */
  [AssetType.BACKGROUND_IMAGE]?: string;
  // For backward compatibility
  [AssetType.BACKGROUND_IMAGE_V0]?: string;
  /** Optional URL for the logo asset */
  [AssetType.LOGO]?: string;
  /** Optional URL for the video trailer asset */
  [AssetType.VIDEO_TRAILER]?: string;
}

/**
 * Interface representing the state of the preview section
 * @interface PreviewSectionState
 * @property {string} url - The URL of the current preview asset
 * @property {string} error - Error message if preview loading fails
 * @property {boolean} isLoading - Loading state indicator
 * @property {PreviewState} activeState - Current preview state (selected/unselected/video)
 */
interface PreviewSectionState {
  urls: Record<PreviewState, string>;
  error?: string;
  isLoading: boolean;
  activeState: PreviewState;
}

interface AssetDetails {
  key?: string;
  file?: File;
}

type PreviewState =
  (typeof PreviewConstants.PREVIEW_LABELS)[keyof typeof PreviewConstants.PREVIEW_LABELS];

export { AssetStatus, InputType };
export type {
  IAssetInputHandler,
  LogoTextState,
  AssetState,
  PreviewState,
  StateConfig,
  PreviewAssets,
  PreviewSectionState,
  AssetDetails,
};
